import { useEffect, useRef } from 'react'
import { message } from 'antd'
import CampaignService from '@forwardflow/services/CampaignService'
import { ENV } from '@forwardflow/helpers/configs/envConfig'
import { OnboardingV2Service } from '@forwardflow/services/onboardingV2.service'
import { useAppStore } from '@forwardflow/store/appStore'

interface AssistantData {
   avatar: string
   assistantName: string
   voiceId: string
   description: string
   designation: string
}

const ELEVENLABS_DEFAULT: AssistantData = {
   avatar: 'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-4.png',
   assistantName: 'Dakotah H',
   voiceId: 'P7x743VjyZEOihNNygQ9',
   description: 'Hello I am Dakotah H, your real state agent',
   designation: 'Sales assistant',
}

const GEMINI_DEFAULT: AssistantData = {
   avatar: 'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-4.png',
   assistantName: 'Sulafat',
   voiceId: 'Sulafat',
   description: 'Hello I am Sulafat, your real state agent',
   designation: 'Sales assistant',
}

const DEFAULT_ASSISTANT = ENV.VOICE_PROVIDER === 'gemini' ? GEMINI_DEFAULT : ELEVENLABS_DEFAULT

const VOICE_AVATARS: Record<string, string> = {
   'P7x743VjyZEOihNNygQ9': 'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-4.png',
   'f5HLTX707KIM4SzJYzSz': 'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-2.png',
   'Sulafat': 'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-4.png',
   'Umbriel': 'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-2.png',
}

const DEFAULT_AVATAR = 'https://forward-flow-assets.s3.us-west-2.amazonaws.com/campaign-avatars/assistant-4.png'

function onboardingAssistantToData(assistant: { voice_id: string; assistant_name: string }): AssistantData {
   return {
      avatar: VOICE_AVATARS[assistant.voice_id] || DEFAULT_AVATAR,
      assistantName: assistant.assistant_name,
      voiceId: assistant.voice_id,
      description: `Hello I am ${assistant.assistant_name}, your real state agent`,
      designation: 'Sales assistant',
   }
}

/**
 * Resolves the assistants to create — from onboarding state or hardcoded default.
 */
export async function resolveDefaultAssistants(organizationId?: string): Promise<AssistantData[]> {
   if (organizationId) {
      try {
         const onboardingState = await OnboardingV2Service.getOnboardingState({
            organizationId,
         })
         const onboardingAssistants =
            onboardingState?.data?.campaignConfiguration?.ai_assistants
         if (onboardingAssistants?.length) {
            return onboardingAssistants.map(onboardingAssistantToData)
         }
      } catch {
         // Fall back to default
      }
   }
   return [DEFAULT_ASSISTANT]
}

interface UseCreateAssistantsProps {
   campaignTitle: string
   aiAssistants?: any[]
   onAssistantsCreated?: () => void
}

export const useCreateAssistants = ({
   campaignTitle,
   aiAssistants,
   onAssistantsCreated,
}: UseCreateAssistantsProps) => {
   const hasCreatedAssistants = useRef(false)
   const { currentOrganization } = useAppStore()

   // Reset guard when assistants exist, so future removal triggers re-creation
   useEffect(() => {
      if (aiAssistants && aiAssistants.length > 0) {
         hasCreatedAssistants.current = false
      }
   }, [aiAssistants])

   useEffect(() => {
      const createAssistants = async () => {
         if (aiAssistants?.length === 0 && !hasCreatedAssistants.current) {
            hasCreatedAssistants.current = true
            try {
               const assistantsToCreate = await resolveDefaultAssistants(currentOrganization)

               const createdAssistants =
                  await CampaignService.createOrganizationCampaignAssistantsInBulk(
                     campaignTitle,
                     assistantsToCreate
                  )

               if (createdAssistants) {
                  onAssistantsCreated?.()
               }
            } catch (error) {
               console.error('Failed to create assistants:', error)
               message.error('Failed to create assistants')
               hasCreatedAssistants.current = false // Reset on error
            }
         }
      }

      if (aiAssistants !== undefined) {
         createAssistants()
      }
   }, [campaignTitle, aiAssistants, onAssistantsCreated, currentOrganization])
}
